/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Client for the Compute Advisor service - the same endpoint and reCAPTCHA v3
// flow the <podstack-advisor> web component uses (dashboard-core/advisor-widget),
// so the /advisor page can render results in the console's own layout.

export const ADVISOR_ENDPOINT = (process.env.REACT_APP_ADVISOR_URL || 'https://advisor.podstack.ai').replace(/\/+$/, '');
// Same public reCAPTCHA v3 site key the widget is mounted with (see AdvisorFab).
export const ADVISOR_SITE_KEY = process.env.REACT_APP_ADVISOR_SITE_KEY ?? '6Ld50FotAAAAABFSMruczVVdhvIYdpXSz8VObAvz';

export type AdvisorProduct = 'trainpods' | 'quickpods' | 'inference' | 'dc-suite';

export interface AdvisorRecommendation {
  rank: number;
  product: AdvisorProduct;
  gpu: string;
  vram_gb: number;
  region: string;
  price_inr_hr: number;
  why: string;
  est_cost_hint: string;
  gpu_fraction: string;
  vcpus: number;
  ram_gb: number;
  storage: string;
  cta_url: string;
}

export interface AdvisorResponse {
  summary: string;
  recommendations: AdvisorRecommendation[];
  assumptions: string[];
  source: 'llm' | 'heuristic';
}

interface Grecaptcha {
  ready(cb: () => void): void;
  execute(sitekey: string, opts: { action: string }): Promise<string>;
}

let scriptLoading: Promise<void> | null = null;

// Load api.js scoped to our key - grecaptcha.execute(key) only works for keys
// api.js was rendered with, even if another api.js is already on the page.
const loadRecaptcha = (sitekey: string): Promise<void> => {
  if (!scriptLoading) {
    scriptLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://www.google.com/recaptcha/api.js?render=${sitekey}`;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptLoading = null;
        reject(new Error('recaptcha script failed'));
      };
      document.head.appendChild(s);
    });
  }
  return scriptLoading;
};

/** reCAPTCHA v3 token, or "dev" when no site key is configured (advisor-service skips the check). */
const getRecaptchaToken = async (sitekey: string): Promise<string> => {
  if (!sitekey) return 'dev';
  await loadRecaptcha(sitekey);
  const g = (window as unknown as { grecaptcha?: Grecaptcha }).grecaptcha;
  if (!g) throw new Error('recaptcha unavailable');
  await new Promise<void>((r) => g.ready(r));
  return g.execute(sitekey, { action: 'recommend' });
};

export const recommendCompute = async (workload: string, signal?: AbortSignal): Promise<AdvisorResponse> => {
  const token = await getRecaptchaToken(ADVISOR_SITE_KEY);
  const res = await fetch(`${ADVISOR_ENDPOINT}/api/v1/recommend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workload: workload.slice(0, 500), recaptcha_token: token }),
    signal,
  });
  if (!res.ok) throw new Error(`advisor: HTTP ${res.status}`);
  return res.json();
};
